import { GIANT_ANCESTRY_ID, GIANT_ANCESTRY_USE, GIANT_ON_HIT, giantAncestryKey, tumbleSizeEligible } from '../../../../shared/rules/speciesTraits.mjs';
import { speciesUseProfile } from '../../../../shared/rules/speciesResources.mjs';
import { getTotalClassLevel } from '../../../../shared/rules/classIdentity.mjs';
import proficiencyBonus from '../../../../utils/proficiencyBonus';

const ineligible = (reasonCode) => ({ status: 'ineligible', featureId: GIANT_ANCESTRY_ID, featureLabel: 'Giant Ancestry', reasonCode });
const optionOf = (character) => GIANT_ON_HIT[giantAncestryKey(character)] || null;

/** The Goliath's on-hit Giant Ancestry option (Fire, Frost or Hill) has uses left. */
export const hasGiantAncestryOnHit = (character) => Boolean(optionOf(character))
  && speciesUseProfile(character, GIANT_ANCESTRY_USE, proficiencyBonus(getTotalClassLevel(character))).remaining > 0;

/**
 * 2024 Giant Ancestry when the Goliath hits with an attack roll: offers the chosen option (Fire's Burn +1d10 Fire,
 * Frost's Chill +1d6 Cold and -10 ft Speed, Hill's Tumble Prone on a Large or smaller target). Optional, so it asks;
 * the claim spends the use on the server, which also applies Frost's or Hill's effect.
 */
export const evaluateGiantAncestry = (context) => {
  const character = context?.attacker?.entity;
  const option = optionOf(character);
  if (!option) return ineligible('no-on-hit-ancestry');
  if (context?.roll?.hit === false) return ineligible('not-a-hit');
  const combat = context?.combat || {};
  const targetId = context?.target?.combatantId;
  if (!combat.campaignId || !Number.isInteger(combat.turnSequence) || !targetId) return ineligible('no-combat');
  const profile = speciesUseProfile(character, GIANT_ANCESTRY_USE, proficiencyBonus(getTotalClassLevel(character)));
  if (profile.remaining < 1) return ineligible('no-uses');
  if (option.prone && !tumbleSizeEligible(context.target?.entity?.temporarySize || context.target?.entity?.size)) return ineligible('target-too-large');
  const base = { featureId: GIANT_ANCESTRY_ID, featureLabel: option.label, reasonCode: 'attack-hit', components: [], claims: [] };
  const claim = { campaignId: combat.campaignId, combatantId: context.attacker.combatantId, featureId: GIANT_ANCESTRY_ID,
    turnSequence: combat.turnSequence, resolutionId: context.resolutionId, targetId };
  const components = option.dice ? [{ id: GIANT_ANCESTRY_ID, label: option.label, source: { type: 'feature', featureId: GIANT_ANCESTRY_ID },
    dice: { ...option.dice }, flat: 0, damageType: { mode: 'explicit', value: option.type }, critical: { dice: 'double' } }] : [];
  return {
    status: 'confirmation-required',
    ...base,
    decision: {
      id: `${context.resolutionId}:${GIANT_ANCESTRY_ID}`, kind: 'confirm-feature', featureId: GIANT_ANCESTRY_ID, resolutionId: context.resolutionId,
      title: option.label, label: option.label,
      prompt: `Use ${option.label}: ${option.text}. ${profile.remaining} of ${profile.maximum} uses left until a Long Rest.`,
      choices: [{ id: 'apply', action: 'apply', label: `Use ${option.label}` }, { id: 'skip', label: 'Not this time' }],
    },
    resolveDecision: (choiceId) => (choiceId === 'apply' ? { ...base, components, claims: [claim] } : base),
  };
};
